import { setCookie, getCookie } from 'cookies-next'
import { httpClient } from '../http-client'

// 90 dias em segundos para expiração dos cookies de autenticação
const COOKIE_MAX_AGE = 90 * 24 * 60 * 60 // 7776000 segundos

/**
 * Tipos de resposta do login com verificações de segurança
 */
export interface LoginSecurityResponse {
  // Login bem-sucedido
  access_token?: string
  refresh_token?: string
  user?: {
    id: string
    email: string
    roles: string[]
    twoFactorEnabled: boolean
    name?: string
    first_name?: string
    last_name?: string
  }
  // Verificações de segurança
  requirePasswordChange?: boolean
  newDeviceVerification?: boolean
  deviceLimitReached?: boolean
  memberAreaBlocked?: boolean
  message?: string
  // Informações do dispositivo
  deviceInfo?: {
    city?: string
    ip?: string
    device?: string
  }
}

/**
 * AuthService - Gerencia autenticação, login, logout, recuperação de senha, 2FA
 */
class AuthService {
  /**
   * Login com verificações de segurança (dispositivo, troca de senha)
   * Pode retornar diferentes estados que precisam ser tratados pelo frontend
   */
  async login(email: string, password: string, mentorId?: string): Promise<LoginSecurityResponse> {
    try {
      const headers = mentorId?.trim() ? { 'mentor-id': mentorId.trim() } : undefined
      const response = await httpClient.post<LoginSecurityResponse>(
        '/auth/login',
        { email, password },
        headers ? { headers } : undefined,
      )
      const data = response.data

      // Verificar estados de segurança
      if (data.requirePasswordChange) {
        // Precisa trocar senha - salvar email temporariamente para usar na tela de troca
        sessionStorage.setItem('pending_password_change_email', email)
        throw new Error('REQUIRE_PASSWORD_CHANGE')
      }

      if (data.newDeviceVerification) {
        // Novo dispositivo detectado - precisa verificar via email
        throw new Error('NEW_DEVICE_VERIFICATION')
      }

      if (data.deviceLimitReached) {
        // Limite de dispositivos atingido
        throw new Error('DEVICE_LIMIT_REACHED')
      }

      if (data.memberAreaBlocked) {
        // Área de membros bloqueada pelo mentor
        throw new Error('MEMBER_AREA_BLOCKED')
      }

      // Login bem-sucedido
      this.persistSession(data)

      return data
    } catch (error: any) {
      // Re-lançar erros de segurança customizados
      if (error.message === 'REQUIRE_PASSWORD_CHANGE' ||
        error.message === 'NEW_DEVICE_VERIFICATION' ||
        error.message === 'DEVICE_LIMIT_REACHED' ||
        error.message === 'MEMBER_AREA_BLOCKED') {
        throw error
      }

      // Tratar erro 401 - credenciais inválidas
      if (error?.response?.status === 401) {
        throw new Error('INVALID_CREDENTIALS')
      }

      // Proxy Next (/api/v1) ou backend offline → axios devolve 500 ou erro de rede
      if (!error?.response) {
        throw new Error('BACKEND_UNAVAILABLE')
      }
      if (error.response.status >= 500) {
        throw new Error('BACKEND_UNAVAILABLE')
      }

      throw error
    }
  }

  /**
   * Stores a successful login response (same shape as /auth/login) in the auth cookies.
   * Shared by the password login and the one-time "paid, now enter" exchange.
   * Returns false when the response does not carry a full session.
   */
  persistSession(data: LoginSecurityResponse): boolean {
    if (!(data.access_token && data.refresh_token && data.user)) return false
    setCookie('access_token', data.access_token, { maxAge: COOKIE_MAX_AGE })
    setCookie('refresh_token', data.refresh_token, { maxAge: COOKIE_MAX_AGE })
    setCookie('user', JSON.stringify(data.user), { maxAge: COOKIE_MAX_AGE })
    return true
  }

  /**
   * Login via Handshake (senha mestre 'integration')
   * Usado para auto-login de outras aplicações
   * Usa endpoint simples sem verificação de dispositivo
   */
  async handshakeLogin(email: string, token: string) {
    // Se o token é 'integration', usa a senha mestre
    if (token === 'integration') {
      // Tenta fazer login com a senha mestre (endpoint simples)
      try {
        const response = await httpClient.post('/auth/login/simple', {
          email,
          password: token
        })
        const { access_token, refresh_token, user } = response.data

        setCookie('access_token', access_token, { maxAge: COOKIE_MAX_AGE })
        setCookie('refresh_token', refresh_token, { maxAge: COOKIE_MAX_AGE })
        setCookie('user', JSON.stringify(user), { maxAge: COOKIE_MAX_AGE })

        return response.data
      } catch (error) {
        console.error('Erro no login com integration:', error)
        throw new Error('Falha no login automático. Token de integração inválido.')
      }
    } else {
      // Se for outro token, usa como senha normal
      return this.login(email, token)
    }
  }

  async logout() {
    try {
      await httpClient.post('/auth/logout')
    } finally {
      // @ts-ignore - accessing protected method
      httpClient.clearAuth()
    }
  }

  async recover(email: string, mentorId?: string) {
    const headers = mentorId ? { 'mentor-id': mentorId } : {}
    const response = await httpClient.post('/auth/recover', { email }, { headers })
    return response.data
  }

  async resetPassword(token: string, password: string) {
    const response = await httpClient.post(`/auth/reset-password?token=${token}`, {
      password,
    })
    return response.data as { email: string, message: string }
  }

  async checkEmailAvailability(email: string) {
    try {
      const response = await httpClient.get(`/users/check-email/${encodeURIComponent(email)}`)
      const isClient = response.data // true ou false

      return {
        available: !isClient,
        email: email.toLowerCase()
      }
    } catch (error) {
      throw new Error('Erro ao verificar disponibilidade do email')
    }
  }

  async register(data: {
    first_name: string
    last_name: string
    email: string
    phone: string
    password: string
    policies: boolean
    active?: boolean
    custom_fields?: Record<string, any>
    guardian_email?: string
    mentorId?: string
  }) {
    const mentorId = data.mentorId
    const url = mentorId ? `/clients?mentorId=${mentorId}` : '/clients'

    const payload = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      phone: data.phone,
      password: data.password,
      policies: data.policies,
      active: data.active ?? true,
      custom_fields: data.custom_fields || {},
      guardian_email: data.guardian_email
    }

    const response = await httpClient.post(url, payload)
    const { user, profile, success, message } = response.data

    // Após registro bem-sucedido, fazer login automático
    // Se a API de registro já retornar tokens, usa eles
    if (response.data.access_token && response.data.refresh_token) {
      setCookie('access_token', response.data.access_token, { maxAge: COOKIE_MAX_AGE })
      setCookie('refresh_token', response.data.refresh_token, { maxAge: COOKIE_MAX_AGE })
      setCookie('user', JSON.stringify(user), { maxAge: COOKIE_MAX_AGE })
    } else {
      // Caso contrário, faz login com email e senha
      await this.login(data.email, data.password, mentorId)
    }

    return {
      success,
      message,
      user,
      profile
    }
  }

  // 2FA
  async enable2FA() {
    const response = await httpClient.post('/auth/2fa/enable')
    return response.data
  }

  async verify2FA(token: string) {
    const response = await httpClient.post('/auth/2fa/verify', { token })
    return response.data
  }

  // Verificação de dispositivo (retorna tokens para login automático)
  async verifyDevice(token: string) {
    const response = await httpClient.post(`/auth/verify-device?token=${token}`)
    const data = response.data as {
      message: string
      email: string
      access_token: string
      refresh_token: string
      user: {
        id: string
        email: string
        roles: string[]
        twoFactorEnabled: boolean
        name?: string
        first_name?: string
        last_name?: string
      }
    }

    // Salvar tokens para login automático
    if (data.access_token && data.refresh_token && data.user) {
      setCookie('access_token', data.access_token, { maxAge: COOKIE_MAX_AGE })
      setCookie('refresh_token', data.refresh_token, { maxAge: COOKIE_MAX_AGE })
      setCookie('user', JSON.stringify(data.user), { maxAge: COOKIE_MAX_AGE })
    }

    return data
  }

  // Troca de senha obrigatória (método legado - mantido para compatibilidade)
  async forcePasswordChange(currentPassword: string, newPassword: string) {
    const response = await httpClient.post('/auth/force-password-change', {
      currentPassword,
      newPassword,
    })
    return response.data as { message: string }
  }

  // Solicitar reset de senha por email (novo fluxo de segurança)
  async requestForcePasswordReset(email: string, mentorId?: string) {
    const headers = mentorId ? { 'mentor-id': mentorId } : {}
    const response = await httpClient.post('/auth/request-force-password-reset', { email }, { headers })
    return response.data as { message: string }
  }

  // Gerenciamento de sessões/dispositivos
  async getActiveSessions() {
    const response = await httpClient.get('/user-sessions')
    return response.data as Array<{
      id: string
      device_id: string
      device_info: string
      ip_address: string
      city: string
      region: string
      country: string
      is_active: boolean
      created_at: string
      last_activity: string
    }>
  }

  async removeSession(sessionId: string) {
    const response = await httpClient.delete(`/user-sessions/${sessionId}`)
    return response.data as { message: string }
  }
}

export const authService = new AuthService()
